import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductApi } from '../../../api/ProductApi';
import {
  PageAbsolutePath,
  PageRelativePath,
  VehicleIdType
} from '../../../consts/Common';
import { PaymentMethod } from '../Constants';
import PageHeader from '../PageHeader';
import {
  DocumentTreeItem,
  ProductData,
  ProductFilter,
  ProductPageCustomisation,
  ProductType,
  QuoteProduct,
  VinWarrantyExpiry
} from '../CommonTypes';
import Products from './Products';
import { ButtonContinue } from '../Button/ButtonContinue';
import { Journey } from '../../../consts/Common';
import { SiteContext } from '../../../context/SiteContext';
import { ButtonBack } from '../Button/ButtonBack';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { ButtonConfirm } from '../Button/ButtonConfirm';
import LoadingContainer from '../LoadingContainer/LoadingContainer';
import { useTranslation } from 'react-i18next';
import { setCountryCode } from '../HelperMethods';
import { getQuotedPrice } from '../ProductMethods';

type ProductManagerProps = {
  products: ProductType[];
  productFilter: ProductFilter;
  foundExistingQuotes: boolean;
  cms: ProductPageCustomisation;
  onUpdateProducts: (
    products: ProductType[],
    updateDocuments?: boolean
  ) => void;
  onUpdateProductDocuments: (documents: DocumentTreeItem[]) => void;
  token: string;
  quoteProducts?: QuoteProduct[];
  hasQuoteExpired: boolean;
  isExpiredMessageAcknowledged: boolean;
  setIsExpiredMessageAcknowledged: (
    isExpiredMessageAcknowledged: boolean
  ) => void;
  showKeyBenefits: boolean;
  showQuotedPrice: boolean;
  expiryDetails?: VinWarrantyExpiry;
  vehicleIdType?: VehicleIdType;
  showKeyBenefitsInProductCard?: boolean;
};

const ProductManager = ({
  products,
  productFilter,
  foundExistingQuotes,
  cms,
  onUpdateProducts,
  onUpdateProductDocuments,
  token,
  quoteProducts,
  hasQuoteExpired,
  isExpiredMessageAcknowledged,
  setIsExpiredMessageAcknowledged,
  showKeyBenefits,
  showQuotedPrice,
  expiryDetails,
  vehicleIdType,
  showKeyBenefitsInProductCard
}: ProductManagerProps): JSX.Element => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { site } = useContext(SiteContext);
  const { journeyType } = site;
  const onCloseModal = () => {
    setShowModal(false);
    setIsExpiredMessageAcknowledged(true);
  };

  const noOfInstallments = Math.max(
    ...products
      .map((p) => p.productVariants)
      .reduce((a, b) => a.concat(b), [])
      .filter((v) => v.paymentMethod === PaymentMethod.Monthly)
      .map((v) => v.noOfInstallments),
    0
  );

  useEffect(() => {
    window.scrollTo(0, 0);

    if (hasQuoteExpired && !isExpiredMessageAcknowledged) {
      setShowModal(true);
    }

    getProducts();
  }, []);

  const makeDefaultSelection = (products: ProductType[]) => {
    if (products.length === 1) {
      const newProducts = products.map((p) => {
        return { ...p, isSelected: true };
      });
      onUpdateProducts(newProducts, true);
    }
  };

  const getProducts = () => {
    const controller = new AbortController();

    const successCallback = (productDetails: ProductData) => {
      const updatedProductDetails = productDetails.products.map((product) => {
        const quotePrice = getQuotedPrice(product, quoteProducts ?? []);
        return {
          ...product,
          isSelected: showQuotedPrice && quotePrice !== undefined,
          productVariants: product.productVariants.map((variant) => ({
            ...variant,
            isSelected: quotePrice?.variantId === variant.variantId
          }))
        };
      });

      onUpdateProductDocuments(productDetails.documents);
      onUpdateProducts(updatedProductDetails, true);
      makeDefaultSelection(productDetails.products);
      setLoading(false);
    };

    const failureCallback = () => {
      setLoading(false);
    };

    if (products.length === 0) {
      if (foundExistingQuotes && quoteProducts?.length === 0) return;

      setLoading(true);

      new ProductApi().getProducts(
        productFilter,
        token,
        successCallback,
        failureCallback,
        controller.signal
      );
    }

    return () => {
      controller.abort();
    };
  };

  const addOrRemoveProduct = (productTypeCode: string) => {
    const newItems = products.map((product) =>
      product.productTypeCode === productTypeCode
        ? {
            ...product,
            isSelected: !product.isSelected,
            policyStartDate: undefined,
            selectedTyreTypeId: undefined,
            selectedClaimLimit: undefined,
            selectedCoverLevelId: undefined,
            selectedPaymentMethod: undefined,
            productVariants: product.productVariants.map((variant) =>
              variant.isSelected === true && !quoteProducts
                ? { ...variant, isSelected: false }
                : variant
            )
          }
        : product
    );

    onUpdateProducts(newItems, true);
  };

  const goToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const onBack = () => {
    if (journeyType === Journey.Customer) {
      navigate(PageRelativePath.VehicleDetails, {
        state: { vehicleIdType: vehicleIdType }
      });
    } else if (
      journeyType === Journey.DealerJourney &&
      foundExistingQuotes &&
      !(quoteProducts && quoteProducts.length > 0)
    ) {
      const path = setCountryCode(PageAbsolutePath.Landing, site.countryCode);
      navigate(path);
    } else {
      navigate(PageRelativePath.VehicleDetails, { state: { expiryDetails } });
    }

    goToTop();
  };

  const onCreatePlan = () => {
    if (journeyType === Journey.Customer) {
      navigate(PageRelativePath.CreatePlan, {
        state: {
          expiryDetails: expiryDetails,
          vehicleIdType: vehicleIdType
        }
      });
    } else {
      navigate(PageRelativePath.CreatePlan);
    }
  };

  const getDescription = (): string => {
    if (cms && products && products.length > 0) {
      return cms.description;
    } else {
      return cms.noProductsFound;
    }
  };

  const isCreatePlanEnabled = () =>
    products.filter((p) => p.isSelected === true).length > 0;

  const informationText = cms?.information
    ? cms.information.replace('{NoOfInstalments}', noOfInstallments.toString())
    : '';

  const { t } = useTranslation();

  return (
    <>
      <LoadingContainer
        loading={loading}
        message={t('loading-placeholder.loading-text')}>
        <div>
          <PageHeader
            title={cms.title}
            description={getDescription()}
            information={informationText || ''}
            warning={cms.warning}
            descriptionClass='p2'
          />
          {foundExistingQuotes && quoteProducts && quoteProducts.length > 0 && (
            <p className='p2'>
              {t('customer-product-page.existing-quotes-found')}
            </p>
          )}
          <Products
            products={products}
            quoteProducts={quoteProducts}
            addOrRemoveProduct={addOrRemoveProduct}
            cms={cms}
            culture={site.cultureCode}
            showKeyBenefits={showKeyBenefits}
            showKeyBenefitsInProductCard={showKeyBenefitsInProductCard}
            showQuotedPrice={showQuotedPrice}
          />
          <div className='flex-row form-nav'>
            <ButtonBack
              name='btnBack'
              onClick={onBack}
              text={t('customer-product-page.back-button-text')}
            />

            <ButtonContinue
              name='btnCreatePlan'
              text={t('customer-product-page.continue-button-text')}
              onClick={onCreatePlan}
              disabled={!isCreatePlanEnabled()}
            />
            <Modal isOpen={showModal}>
              <ModalHeader
                toggle={onCloseModal}
                className='modal-header-title cms-modal-header-title'>
                <span>{t('customer-product-page.quote-expired-heading')}</span>
                <span
                  className='close'
                  aria-hidden='true'
                  onClick={onCloseModal}>
                  ×
                </span>
              </ModalHeader>
              <ModalBody className='show-more-container'>
                <p
                  className='p2'
                  style={{
                    marginLeft: '15px'
                  }}>
                  {t('customer-product-page.quote-expired-recalculate')}
                </p>
              </ModalBody>
              <ModalFooter>
                <div className='flex-row button-row'>
                  <ButtonConfirm
                    name='btn-confirm-manual-vehicle'
                    text={t(
                      'customer-product-page.button-quote-expired-continue'
                    )}
                    additionalClassNames='m05-left'
                    onClick={onCloseModal}
                  />
                </div>
              </ModalFooter>
            </Modal>
          </div>
        </div>
      </LoadingContainer>
    </>
  );
};

export default ProductManager;
